import { useState, useEffect, useRef } from 'react';
import { 
  Clock, AlertCircle, CheckCircle, XCircle, Lightbulb, 
  ArrowLeft, Trophy, Target, Zap, Flag, Bookmark, Eye, Play, Sparkles
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExamMode({ onBack, user, hasActiveSubscription, onPayment, onExamComplete }) {
  const [examState, setExamState] = useState('setup'); // setup, exam, results
  const [isTimedMode, setIsTimedMode] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [markedQuestions, setMarkedQuestions] = useState(new Set()); // İşaretli sorular
  const [timeRemaining, setTimeRemaining] = useState(50 * 60); // 50 minutes in seconds
  const [startTime, setStartTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showAnswersImmediately, setShowAnswersImmediately] = useState(false); // Cevapları anında göster
  const [showFinishConfirm, setShowFinishConfirm] = useState(false); // Bitirme onay modalı
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(false); // Başlangıç animasyonu
  const [timeExpired, setTimeExpired] = useState(false); // Süre doldu flag
  
  // Ref for auto-scrolling to question on navigation
  const questionContainerRef = useRef(null);

  // Auto-scroll to question when currentQuestionIndex changes
  useEffect(() => {
    if (examState === 'exam' && questionContainerRef.current) {
      setTimeout(() => {
        questionContainerRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
      }, 100);
    }
  }, [currentQuestionIndex, examState]);

  // Timer effect - must be called unconditionally (before any early returns)
  useEffect(() => {
    if (!hasActiveSubscription) return; // Skip if not premium
    if (examState === 'exam' && isTimedMode && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimeExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [examState, isTimedMode, timeRemaining, hasActiveSubscription]);
  
  // Premium kontrolü - Sınav modu sadece premium üyeler için
  if (!hasActiveSubscription) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-red-950 via-red-800 to-red-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-blob top-0 left-0"></div>
          <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl animate-blob animation-delay-2000 top-0 right-0"></div>
        </div>
        
        <div className="relative z-10 max-w-2xl w-full">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri Dön</span>
          </button>
          
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-3xl blur-xl opacity-75 group-hover:opacity-100 transition duration-300 animate-pulse"></div>
            <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-12 shadow-2xl text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-red-500 to-red-600 rounded-full mb-6 shadow-lg">
                <Trophy className="w-12 h-12 text-white" />
              </div>
              
              <h2 className="text-4xl font-black text-white mb-4">
                🔒 Sınav Modu - Premium Özellik
              </h2>
              
              <p className="text-xl text-white/90 mb-8">
                70 soruluk gerçek sınav deneyimi için Premium üyelik gereklidir.
              </p>
              
              <div className="bg-white/10 rounded-2xl p-6 mb-8 text-left">
                <h3 className="text-xl font-black text-white mb-4 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-yellow-400" />
                  Premium ile Neler Kazanırsınız?
                </h3>
                <div className="space-y-3 text-white/90">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>70 soruluk tam sınav deneyimi</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Gerçek sınav koşullarında pratik</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Detaylı sonuç analizi</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Sınırsız deneme hakkı</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>700+ soru havuzuna tam erişim</span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={onPayment}
                className="w-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 hover:from-yellow-500 hover:via-orange-600 hover:to-red-600 text-white px-8 py-5 rounded-2xl font-black text-2xl transition-all duration-300 shadow-2xl hover:scale-105 flex items-center justify-center gap-3"
              >
                <Trophy className="w-8 h-8" />
                Premium Satın Al
                <Zap className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startExam = async () => {
    setLoading(true);
    try {
      // Check if admin test mode
      const adminPassword = localStorage.getItem('admin_password');
      const config = {};
      
      if (adminPassword) {
        config.headers = {
          'X-Admin-Password': adminPassword
        };
      }
      
      const response = await axios.get(`${API}/questions/exam/random?count=70`, config);
      if (response.data.length < 70) {
        alert(`Sadece ${response.data.length} soru bulundu. Devam edilsin mi?`);
      }
      setQuestions(response.data);
      setUserAnswers(new Array(response.data.length).fill(null));
      setStartTime(new Date());
      setExamState('exam');
    } catch (error) {
      console.error('Sınav soruları yüklenemedi:', error);
      alert('Sınav başlatılırken bir hata oluştu.');
    }
    setLoading(false);
  };

  const handleAnswer = (answer) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = answer;
    setUserAnswers(newAnswers);
  };


  const handleReportQuestion = async () => {
    if (!feedbackMessage.trim()) {
      alert('Lütfen bir mesaj girin.');
      return;
    }

    try {
      await axios.post(
        `${API}/questions/${currentQuestion.id}/feedback`,
        {
          type: 'report',
          message: feedbackMessage
        },
        { withCredentials: true }
      );
      
      alert('✅ Geri bildiriminiz alındı, teşekkür ederiz!');
      setShowFeedbackModal(false);
      setFeedbackMessage('');
    } catch (error) {
      console.error('Feedback error:', error);
      alert('❌ Geri bildirim gönderilemedi.');
    }
  };


  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  // Internal finish function
  const finishExamInternal = async () => {
    if (questions.length === 0 || !startTime) return;
    
    const endTime = new Date();
    const timeTaken = Math.floor((endTime - startTime) / 1000);
    
    // Calculate results
    let correct = 0;
    questions.forEach((q, idx) => {
      if (userAnswers[idx] === q.correct_answer) {
        correct++;
      }
    });

    const scorePercentage = ((correct / questions.length) * 100).toFixed(1);

    // Save exam result with user_id
    try {
      await axios.post(`${API}/exam/results`, {
        user_id: user?.id || user?.user_id,
        user_email: user?.email,
        exam_type: 'exam',
        is_timed: isTimedMode,
        time_taken_seconds: timeTaken,
        total_questions: questions.length,
        correct_answers: correct,
        wrong_answers: questions.length - correct,
        score_percentage: parseFloat(scorePercentage),
        questions_answered: questions.map((q, idx) => ({
          question_id: q.id,
          category_id: q.category_id,
          user_answer: userAnswers[idx] || '',
          is_correct: userAnswers[idx] === q.correct_answer
        }))
      });
      console.log('✅ Sınav sonucu kaydedildi');
    } catch (error) {
      console.error('Sınav sonucu kaydedilemedi:', error);
    }

    // Save marked questions to backend
    if (markedQuestions.size > 0) {
      try {
        const markedIndices = Array.from(markedQuestions);
        for (const idx of markedIndices) {
          const question = questions[idx];
          if (question) {
            await axios.post(`${API}/user/mark-question/${question.id}`, {}, { withCredentials: true });
          }
        }
        console.log(`✅ ${markedQuestions.size} işaretli soru kaydedildi`);
      } catch (error) {
        console.error('İşaretli sorular kaydedilemedi:', error);
      }
    }

    setExamState('results');
    setTimeExpired(false); // Reset flag
    
    // Notify parent component that exam is complete
    if (onExamComplete) {
      setTimeout(() => onExamComplete(), 500);
    }
  };

  const finishExam = async () => {
    await finishExamInternal();
  };

  // Handle time expiration - check every render if needed
  if (timeExpired && examState === 'exam') {
    // This will trigger on next render when timeExpired becomes true
    finishExamInternal();
  }

  const calculateResults = () => {
    let correct = 0;
    let unanswered = 0;
    questions.forEach((q, idx) => {
      if (!userAnswers[idx]) {
        unanswered++;
      } else if (userAnswers[idx] === q.correct_answer) {
        correct++;
      }
    });
    const percentage = ((correct / questions.length) * 100).toFixed(1);
    const passed = correct >= 52; // Minimum 52 correct answers (74%)
    return {
      correct,
      wrong: questions.length - correct - unanswered,
      unanswered,
      percentage,
      passed
    };
  };

  // Setup Screen - Clean Red Theme
  if (examState === 'setup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Geri Dön</span>
          </button>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-red-500 to-red-700 rounded-full mb-6 shadow-lg">
                <Trophy className="w-12 h-12 text-white" />
              </div>
              
              <h1 className="text-5xl font-black text-white mb-4">
                🏆 Sınav Modu
              </h1>
              <p className="text-xl text-white/80">70 Soruluk Tam Sınav Deneyimi</p>
              <div className="flex justify-center gap-4 mt-4">
                <span className="px-3 py-1 bg-white/10 text-white rounded-full text-sm border border-white/20">🇸🇪 İsveç Formatı</span>
                <span className="px-3 py-1 bg-white/10 text-white rounded-full text-sm border border-white/20">⏱️ 50 Dakika</span>
                <span className="px-3 py-1 bg-white/10 text-white rounded-full text-sm border border-white/20">📊 Geçme: %74</span>
              </div>
            </div>

            {/* Mode Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => setIsTimedMode(true)}
                className={`text-left p-6 rounded-2xl border-2 transition-all ${
                  isTimedMode
                    ? 'bg-red-600 border-red-400 shadow-lg'
                    : 'bg-white/5 border-white/20 hover:bg-white/10 hover:border-white/30'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    isTimedMode ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    <Clock className={`w-7 h-7 ${isTimedMode ? 'text-white' : 'text-white/70'}`} />
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold mb-1 ${isTimedMode ? 'text-white' : 'text-white/80'}`}>
                      ⏱️ Zamanlı Sınav
                    </h3>
                    <p className={`text-sm ${isTimedMode ? 'text-white/90' : 'text-white/60'}`}>
                      50 dakika süre limiti
                    </p>
                  </div>
                  {isTimedMode && <CheckCircle className="w-6 h-6 text-green-400 ml-auto" />}
                </div>
              </button>

              <button
                onClick={() => setIsTimedMode(false)}
                className={`text-left p-6 rounded-2xl border-2 transition-all ${
                  !isTimedMode
                    ? 'bg-green-600 border-green-400 shadow-lg'
                    : 'bg-white/5 border-white/20 hover:bg-white/10 hover:border-white/30'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    !isTimedMode ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    <Zap className={`w-7 h-7 ${!isTimedMode ? 'text-white' : 'text-white/70'}`} />
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold mb-1 ${!isTimedMode ? 'text-white' : 'text-white/80'}`}>
                      ⚡ Zamansız Sınav
                    </h3>
                    <p className={`text-sm ${!isTimedMode ? 'text-white/90' : 'text-white/60'}`}>
                      Kendi hızınızda ilerleyin
                    </p>
                  </div>
                  {!isTimedMode && <CheckCircle className="w-6 h-6 text-green-400 ml-auto" />}
                </div>
              </button>
            </div>

            {/* Show Answers Toggle */}
            <div className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/10 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <span className="text-white font-bold text-lg">Cevapları Anında Göster</span>
                  <p className="text-white/50 text-sm">Her sorudan sonra doğru cevabı göster</p>
                </div>
              </div>
              <button
                onClick={() => setShowAnswersImmediately(!showAnswersImmediately)}
                className={`w-16 h-8 rounded-full transition-all ${
                  showAnswersImmediately ? 'bg-green-500' : 'bg-white/20'
                }`}
              >
                <div className={`w-6 h-6 bg-white rounded-full transition-transform shadow-md ${
                  showAnswersImmediately ? 'translate-x-9' : 'translate-x-1'
                }`}></div>
              </button>
            </div>

            {/* Info Box */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Sınav Bilgileri
              </h3>
              <div className="grid grid-cols-2 gap-3 text-white/90 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span> 70 soru rastgele seçilir
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span> Her soru 4 seçenek
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span> Geçme notu: 52 doğru (%74)
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span> Sonuçlar kaydedilir
                </div>
              </div>
            </div>

            {/* Start Button */}
            <button
              onClick={startExam}
              disabled={loading}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white px-8 py-6 rounded-2xl font-black text-2xl transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Hazırlanıyor...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-3">
                  <Play className="w-8 h-8" />
                  Sınava Başla
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (examState === 'results') {
    const results = calculateResults();
    const timeTaken = startTime ? Math.floor((new Date() - startTime) / 1000) : 0;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl">
            <h2 className="text-5xl font-black text-white text-center mb-12">
              Sınav Sonuçları
            </h2>
            
            {/* Pass/Fail Banner */}
            <div className={`relative rounded-3xl p-10 text-white mb-10 overflow-hidden shadow-2xl ${
              results.passed 
                ? 'bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600' 
                : 'bg-gradient-to-br from-red-600 via-pink-600 to-rose-600'
            }`}>
              <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
              <div className="relative z-10 text-center">
                <div className="inline-flex items-center justify-center w-32 h-32 bg-white/20 backdrop-blur-xl rounded-full mb-4">
                  {results.passed ? (
                    <Trophy className="w-16 h-16" />
                  ) : (
                    <Target className="w-16 h-16" />
                  )}
                </div>
                <div className="text-8xl font-black mb-2">{results.percentage}%</div>
                <div className="text-3xl font-bold mb-4">
                  {results.passed ? '🎉 TEBRİKLER! BAŞARILI!' : '❌ BAŞARISIZ'}
                </div>
                <div className="text-xl">
                  {results.passed 
                    ? 'Sınavı başarıyla tamamladınız!' 
                    : 'Geçme notu: En az 52 doğru - Tekrar deneyebilirsiniz'}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <div className="bg-green-500/20 border border-green-400/30 rounded-2xl p-6 text-center">
                <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                <div className="text-4xl font-black text-white mb-1">{results.correct}</div>
                <div className="text-white/70 font-semibold">Doğru</div>
              </div>
              <div className="bg-red-500/20 border border-red-400/30 rounded-2xl p-6 text-center">
                <XCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
                <div className="text-4xl font-black text-white mb-1">{results.wrong}</div>
                <div className="text-white/70 font-semibold">Yanlış</div>
              </div>
              <div className="bg-orange-500/20 border border-orange-400/30 rounded-2xl p-6 text-center">
                <AlertCircle className="w-10 h-10 text-orange-400 mx-auto mb-2" />
                <div className="text-4xl font-black text-white mb-1">{results.unanswered}</div>
                <div className="text-white/70 font-semibold">Boş</div>
              </div>
              <div className="bg-blue-600/20 border border-blue-400/30 rounded-2xl p-6 text-center">
                <Clock className="w-10 h-10 text-red-400 mx-auto mb-2" />
                <div className="text-4xl font-black text-white mb-1">{formatTime(timeTaken)}</div>
                <div className="text-white/70 font-semibold">Süre</div>
              </div>
            </div>

            {/* Question Review */}
            <div className="space-y-6 mb-10 max-h-[600px] overflow-y-auto">
              <h3 className="text-3xl font-bold text-white mb-6 flex items-center gap-3 sticky top-0 bg-indigo-950/80 backdrop-blur-xl py-3 z-10">
                <Lightbulb className="w-8 h-8 text-white" />
                Soru Detayları
              </h3>
              {questions.map((question, idx) => {
                const userAnswer = userAnswers[idx];
                const isCorrect = userAnswer === question.correct_answer;
                const isUnanswered = !userAnswer;
                return (
                  <div key={idx} className={`bg-white/10 backdrop-blur-xl border-l-4 ${
                    isUnanswered ? 'border-orange-400' : isCorrect ? 'border-green-400' : 'border-red-400'
                  } rounded-2xl p-6`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {isUnanswered ? (
                          <AlertCircle className="w-8 h-8 text-orange-400 mt-1" />
                        ) : isCorrect ? (
                          <CheckCircle className="w-8 h-8 text-green-400 mt-1" />
                        ) : (
                          <XCircle className="w-8 h-8 text-red-400 mt-1" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-white/20 px-3 py-1 rounded-lg text-white font-bold text-sm">
                            Soru {idx + 1}
                          </span>
                          {isUnanswered && (
                            <span className="bg-orange-500/30 px-3 py-1 rounded-lg text-orange-200 font-semibold text-sm">
                              Cevaplanmadı
                            </span>
                          )}
                        </div>
                        <h4 className="text-white font-bold mb-3">{question.question_text}</h4>
                        <div className="space-y-2 mb-3">
                          {question.choices.map((choice) => {
                            const isUserChoice = choice.letter === userAnswer;
                            const isCorrectChoice = choice.letter === question.correct_answer;
                            return (
                              <div
                                key={choice.letter}
                                className={`px-4 py-2 rounded-xl ${
                                  isCorrectChoice
                                    ? 'bg-green-500/20 border border-green-400/50'
                                    : isUserChoice
                                    ? 'bg-red-500/20 border border-red-400/50'
                                    : 'bg-white/5 border border-white/10'
                                }`}
                              >
                                <span className="font-bold text-white">{choice.letter}.</span>
                                <span className="text-white/90 ml-2">{choice.text}</span>
                                {isCorrectChoice && <span className="text-green-400 ml-2">✓ Doğru Cevap</span>}
                                {isUserChoice && !isCorrectChoice && <span className="text-red-400 ml-2">✗ Sizin Cevabınız</span>}
                              </div>
                            );
                          })}
                        </div>
                        <div className="bg-blue-600/20 border border-blue-400/30 rounded-xl p-4">
                          <div className="text-red-300 text-sm font-semibold mb-1">Açıklama:</div>
                          <div className="text-white/80 text-sm">{question.explanation}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={onBack}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Exam Screen
  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = userAnswers[currentQuestionIndex];
  const answeredCount = userAnswers.filter(a => a !== null).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 py-8">
      <div className="container mx-auto px-4">
        {/* Top Bar */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                if (window.confirm('Sınavdan çıkmak istediğinize emin misiniz? İlerlemeniz kaydedilmeyecek.')) {
                  onBack();
                }
              }}
              className="text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="text-white">
              <div className="text-sm text-white/70">Sınav Modu</div>
              <div className="font-bold">Soru {currentQuestionIndex + 1} / {questions.length}</div>
            </div>
          </div>
          
          {isTimedMode && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
              timeRemaining < 300 ? 'bg-red-500/20 border border-red-400/50' : 'bg-blue-600/20 border border-blue-400/30'
            }`}>
              <Clock className={`w-5 h-5 ${timeRemaining < 300 ? 'text-red-400' : 'text-red-400'}`} />
              <span className={`font-bold text-lg ${timeRemaining < 300 ? 'text-red-300' : 'text-white'}`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
          )}
          
          <div className="text-white text-right">
            <div className="text-sm text-white/70">Cevaplanan</div>
            <div className="font-bold">{answeredCount} / {questions.length}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Navigator */}
          <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sticky top-4">
              <h3 className="text-white font-bold mb-3">Soru Listesi</h3>
              <div className="flex gap-2 mb-3 text-xs flex-wrap">
                <span className="flex items-center gap-1 text-green-300">
                  <span className="w-3 h-3 bg-green-500 rounded"></span> Doğru
                </span>
                <span className="flex items-center gap-1 text-red-300">
                  <span className="w-3 h-3 bg-red-500 rounded"></span> Yanlış
                </span>
                <span className="flex items-center gap-1 text-yellow-300">
                  <span className="w-3 h-3 bg-yellow-500 rounded"></span> İşaretli
                </span>
              </div>
              <div className="grid grid-cols-7 gap-2 max-h-[600px] overflow-y-auto">
                {questions.map((q, idx) => {
                  const userAnswer = userAnswers[idx];
                  const isCorrect = userAnswer && userAnswer === q.correct_answer;
                  const isWrong = userAnswer && userAnswer !== q.correct_answer;
                  const isMarked = markedQuestions.has(idx);
                  const isCurrent = idx === currentQuestionIndex;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => goToQuestion(idx)}
                      className={`w-10 h-10 rounded-lg font-bold text-sm transition-all relative ${
                        isCurrent
                          ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg scale-110 ring-2 ring-white/50'
                          : isMarked && !userAnswer
                          ? 'bg-yellow-500 text-gray-900 border-2 border-yellow-300'
                          : isMarked && isCorrect
                          ? 'bg-green-500 text-white border-2 border-yellow-400'
                          : isMarked && isWrong
                          ? 'bg-red-500 text-white border-2 border-yellow-400'
                          : isCorrect
                          ? 'bg-green-500 text-white border border-green-400'
                          : isWrong
                          ? 'bg-red-500 text-white border border-red-400'
                          : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                      }`}
                    >
                      {idx + 1}
                      {isMarked && (
                        <span className="absolute -top-1 -right-1 text-xs">🔖</span>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Stats Summary */}
              <div className="mt-4 pt-4 border-t border-white/10 space-y-2 text-sm">
                <div className="flex justify-between text-white/70">
                  <span>Cevaplanan:</span>
                  <span className="text-green-400 font-bold">{answeredCount}/{questions.length}</span>
                </div>
                <div className="flex justify-between text-white/70">
                  <span>Doğru:</span>
                  <span className="text-green-400 font-bold">{userAnswers.filter((a, i) => a && a === questions[i]?.correct_answer).length}</span>
                </div>
                <div className="flex justify-between text-white/70">
                  <span>Yanlış:</span>
                  <span className="text-red-400 font-bold">{userAnswers.filter((a, i) => a && a !== questions[i]?.correct_answer).length}</span>
                </div>
                <div className="flex justify-between text-white/70">
                  <span>İşaretli:</span>
                  <span className="text-yellow-400 font-bold">{markedQuestions.size}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Question Content */}
          <div className="lg:col-span-3">
            <div ref={questionContainerRef} className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 md:p-8 mb-6 scroll-mt-4">
              {/* Action buttons - İşaretle ve Hata Bildir */}
              <div className="flex items-center justify-end gap-2 mb-4">
                <button
                  onClick={() => {
                    const newMarked = new Set(markedQuestions);
                    if (newMarked.has(currentQuestionIndex)) {
                      newMarked.delete(currentQuestionIndex);
                    } else {
                      newMarked.add(currentQuestionIndex);
                    }
                    setMarkedQuestions(newMarked);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
                    markedQuestions.has(currentQuestionIndex)
                      ? 'bg-yellow-500 text-gray-900'
                      : 'bg-white/10 hover:bg-yellow-500 text-white hover:text-gray-900 border border-white/20'
                  }`}
                  title="Soruyu işaretle"
                >
                  <Bookmark className={`w-5 h-5 ${markedQuestions.has(currentQuestionIndex) ? 'fill-current' : ''}`} />
                  {markedQuestions.has(currentQuestionIndex) ? 'İşaretli' : 'İşaretle'}
                </button>
                <button
                  onClick={() => setShowFeedbackModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-red-500 text-white rounded-xl transition-all border border-white/20"
                  title="Hata bildir"
                >
                  <Flag className="w-5 h-5" />
                  Hata Bildir
                </button>
              </div>
              
              <div className="flex items-start justify-between mb-6">
                <h3 className="text-2xl font-bold text-white flex-1">{currentQuestion.question_text}</h3>
              </div>
              <div className="space-y-3">
                {currentQuestion.choices.map((choice) => (
                  <button
                    key={choice.letter}
                    onClick={() => handleAnswer(choice.letter)}
                    className={`w-full text-left px-6 py-4 rounded-2xl font-semibold transition-all ${
                      currentAnswer === choice.letter
                        ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg scale-105'
                        : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    <span className="font-bold">{choice.letter}.</span>
                    <span className="ml-2">{choice.text}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Feedback Modal */}
            {showFeedbackModal && (
              <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-900 border border-white/20 rounded-2xl max-w-md w-full p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Flag className="w-6 h-6 text-red-400" />
                    Soruyu Bildir
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Bu soruda bir sorun mu var? Lütfen detaylı açıklayın.
                  </p>
                  <textarea
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    placeholder="Sorunun ne olduğunu açıklayın..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[100px] mb-4"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={handleReportQuestion}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-semibold transition-all"
                    >
                      Gönder
                    </button>
                    <button
                      onClick={() => {
                        setShowFeedbackModal(false);
                        setFeedbackMessage('');
                      }}
                      className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
                    >
                      İptal
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col gap-4">
              {/* Show Answer Feedback if enabled */}
              {showAnswersImmediately && currentAnswer && (
                <div className={`rounded-xl p-4 border-2 ${
                  currentAnswer === currentQuestion.correct_answer
                    ? 'bg-green-500/20 border-green-400'
                    : 'bg-red-500/20 border-red-400'
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    {currentAnswer === currentQuestion.correct_answer ? (
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-400" />
                    )}
                    <span className={`font-bold ${
                      currentAnswer === currentQuestion.correct_answer ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {currentAnswer === currentQuestion.correct_answer ? '✅ Doğru!' : `❌ Yanlış! Doğru cevap: ${currentQuestion.correct_answer}`}
                    </span>
                  </div>
                  {currentQuestion.explanation && (
                    <p className="text-white/80 text-sm">{currentQuestion.explanation}</p>
                  )}
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-4">
                <button
                  onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentQuestionIndex === 0}
                  className="flex-1 bg-white/10 text-white px-6 py-3 rounded-2xl font-semibold hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Önceki
                </button>
                
                <button
                  onClick={() => setShowFinishConfirm(true)}
                  className="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-2xl font-semibold hover:shadow-2xl transition-all flex items-center gap-2"
                >
                  🏁 Bitir
                </button>
                
                <button
                  onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                  disabled={currentQuestionIndex === questions.length - 1}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-2xl font-semibold hover:shadow-2xl transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sonraki →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Finish Confirmation Modal */}
      {showFinishConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-white/20 rounded-2xl max-w-md w-full p-6 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Sınavı Bitirmek İstiyor musunuz?</h3>
            <p className="text-gray-400 mb-2">
              {answeredCount} / {questions.length} soru cevaplandı
            </p>
            {markedQuestions.size > 0 && (
              <p className="text-yellow-400 mb-4">
                ⚠️ {markedQuestions.size} işaretli soru var
              </p>
            )}
            {answeredCount < questions.length && (
              <p className="text-red-400 mb-4">
                ❗ {questions.length - answeredCount} soru cevaplanmadı
              </p>
            )}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowFinishConfirm(false)}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-all"
              >
                Devam Et
              </button>
              <button
                onClick={() => {
                  setShowFinishConfirm(false);
                  finishExam();
                }}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all"
              >
                Sınavı Bitir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
